import { useEffect, useState } from 'react';
import { Col, Row } from 'antd';
import { makeStyles } from '@mui/styles';

import {
  InitialPage,
  InitialCard,
  InitialJson,
  BlueButton,
} from '@components';
import { CirculoProfile, SyntageAuth, User } from '@types';
import { circuloHttp, syntageAuthHttp } from '@network';
import { dateFormatter } from '@utils';
import { useRequest } from '@hooks';

export const UserCreditBureauPage = ({ user }: {user: User}) => {
  const [profile, setProfile] = useState<CirculoProfile>();
  const [syntageAuth, setSyntageAuth] = useState<SyntageAuth | null>();
  const cls = useStyles();

  const isCompany = user.isCompany;
  const backTo = `/user/${user.id}?initial`;

  const loadProfile = async () => {
    if (!user) return;
    const nextProfile = await circuloHttp.getProfileByUserId(user.id);
    setProfile(nextProfile);
  };

  const loadSyntageAuth = async () => {
    if (!user) return;
    const nextProfile = await syntageAuthHttp.getAuth(user.id);
    setSyntageAuth(nextProfile);
  };

  useEffect(() => void Promise.all([
    loadProfile(),
    loadSyntageAuth(),
  ]), [user]);

  const { loading, submit: checkCirculoProfile } = useRequest(async () => {
    const nextProfile = await circuloHttp.check(user.id);
    setProfile(nextProfile);
  });

  const title = (
    <div className={cls.title}>
      <div>Client</div>
      <div className={cls.titleBadge}>{isCompany ? 'PM' : 'PF'}</div>
    </div>
  );

  return (
    <InitialPage title="Credit Bureau" backTo={backTo} backText="Back to Individual User" gap={24}>
      <Row gutter={[20, 20]}>
        <Col xs={24} sm={24} md={12}>
          <InitialCard extraPadding autoHeight left={title} bordered>
            <Row gutter={[20, 20]}>
              <Col xs={24} sm={24} md={8} className={cls.label}>Name</Col>
              <Col xs={24} sm={24} md={16}>{isCompany ? user.companyName : user.fullName}</Col>
            </Row>
            <Row gutter={[20, 20]}>
              <Col xs={24} sm={24} md={8} className={cls.label}>RFC</Col>
              <Col xs={24} sm={24} md={16}>{isCompany ? user.rfcCompany : user.rfc}</Col>
            </Row>
            <Row gutter={[20, 20]}>
              <Col xs={24} sm={24} md={8} className={cls.label}>CDC authorization</Col>
              <Col xs={24} sm={24} md={16}>
                {syntageAuth?.isActive ? (
                  <div className={cls.green}>
                    Valid • {dateFormatter.toDateSlash(syntageAuth.updatedAt, true)}
                  </div>
                ) : <div className={cls.red}>Inactive</div>}
              </Col>
            </Row>
            <Row gutter={[20, 20]}>
              <Col xs={24} sm={24} md={8} className={cls.label}>Last report</Col>
              <Col xs={24} sm={24} md={16}>
                {dateFormatter.toDateSlash(isCompany ? profile?.pmAt : profile?.pfAt, true)}
                {profile?.pmResponse?.folioConsulta ? (
                  ` • Folio ${profile?.pmResponse?.folioConsulta}`
                ) : null}
                {profile?.pfResponse?.folioConsulta ? (
                  ` • Folio ${profile?.pmResponse?.folioConsulta}`
                ) : null}
              </Col>
            </Row>
            <br />
            <BlueButton green extraPadding loading={loading} onClick={checkCirculoProfile}>
              Request new report
            </BlueButton>
          </InitialCard>
        </Col>
        <Col xs={24} sm={24} md={12}>
          <InitialCard extraPadding autoHeight left={'Credit Information'} bordered minHeight={235}>

          </InitialCard>
        </Col>
      </Row>
      <Row gutter={[20, 20]}>
        <Col xs={24} sm={24} md={8}>
          <InitialJson title="Request" bordered opened
            json={isCompany ? profile?.pmRequest : profile?.pfRequest} />
        </Col>
        <Col xs={24} sm={24} md={16}>
          {isCompany && profile?.pmError ? (
            <InitialJson title="Response ERROR Json"
              bordered opened json={profile?.pmError} isError />
          ) : null}
          {isCompany && profile?.pmResponse ? (
            <InitialJson title="Response RAW Json"
              bordered opened json={profile?.pmResponse} />
          ) : null}
          {!isCompany && profile?.pfError ? (
            <InitialJson title="Response ERROR Json"
              bordered opened json={profile?.pfError} isError />
          ) : null}
          {!isCompany && profile?.pfResponse ? (
            <InitialJson title="Response RAW Json"
              bordered opened json={profile?.pfResponse} />
          ) : null}
        </Col>
      </Row>
    </InitialPage>
  );
};

const useStyles = makeStyles({
  title: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '20px',
    paddingBottom: 8,
  },
  titleBadge: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: 40,
    height: 24,
    borderRadius: 12,
    backgroundColor: '#042E6B',
    color: '#FFF',
    marginLeft: 12,
    fontSize: '12px',
  },
  label: {
    fontSize: '14px',
    fontWeight: 'bold',
    display: 'flex',
    alignItems: 'center',
    height: 22,
  },
  card: {
    marginBottom: 30,
  },
  green: {
    color: '#005F3A',
  },
  red: {
    color: 'red',
  },
});
